/**
 * Which joint poses the part of the robot under the finger.
 *
 * Reading a chip row where three modules all offer a joint called "joint1"
 * is the hard way to find the elbow; pointing at the elbow is the easy one.
 * Meshes carry the URDF link they draw and the module instance they belong
 * to, and a joint knows the link it moves — matching the two is the whole
 * mapping.
 */

import type { InstanceData, JointData } from "../protocol.generated";

/** What a mesh knows about where it belongs, as its userData carries it. */
export interface RobotPart {
  instanceId: string | null;
  linkName: string | null;
}

/**
 * The joint whose child link this part is, if there is one.
 *
 * Joint names are `<prefix>/<joint>`, where the prefix is the module's own
 * (its instance id unless it was renamed), so the instance is what keeps two
 * copies of the same module apart. A part with no joint above it — the base
 * link a module hangs from — matches nothing, which leaves the tap free to
 * mean something else.
 */
export function jointForPart(
  part: RobotPart,
  joints: JointData[],
  instances: InstanceData[]
): JointData | null {
  if (!part.linkName) {
    return null;
  }
  const prefixes = prefixesForInstance(part.instanceId, instances);
  for (const joint of joints) {
    if (joint.child_link_name !== part.linkName) {
      continue;
    }
    if (prefixes.length === 0) {
      // Nothing said which module the mesh came from: a link name alone is
      // still right whenever it is unique, which is the single-module case.
      return joint;
    }
    const prefix = joint.name.split("/")[0];
    if (prefixes.includes(prefix)) {
      return joint;
    }
  }
  return null;
}

/** The prefixes a joint of this instance can be named with. */
function prefixesForInstance(instanceId: string | null, instances: InstanceData[]): string[] {
  if (!instanceId) {
    return [];
  }
  const instance = instances.find((candidate) => candidate.instance_id === instanceId);
  // The id is always a valid prefix; a renamed module answers to its own
  // prefix as well, and the joint list uses whichever the module reports.
  const prefixes = [instanceId];
  if (instance?.prefix) {
    prefixes.push(instance.prefix);
  }
  return prefixes;
}
